import * as THREE from 'three';
import { settings } from '@/config';
import { toVector3 } from '@/utils/math';
import type { Resizable, SizesState } from '@/types';

/**
 * Owns the render PerspectiveCamera and handles responsive aspect/FOV.
 * Motion is delegated entirely to {@link CameraController} — this class never
 * animates itself, keeping the two concerns independent.
 */
export class Camera implements Resizable {
  readonly instance: THREE.PerspectiveCamera;
  private baseFov: number;
  /** When set (Blender camera adopted), preserve its authored framing across aspects. */
  private referenceAspect: number | null = null;

  constructor(sizes: SizesState) {
    const cfg = settings.camera;
    // BOOT AT THE AUTHORED FOCAL LENGTH (2026-07-21, user: "by default camera focal length is
    // 35 — even before the preloader"). When `focalLength` is set, the camera is constructed
    // at exactly that lens against the REAL viewport aspect — the same maths every live
    // per-section focal change uses (`computeFovForFocalLength`) — so "35" means one number
    // everywhere and nothing eases at boot or during the 0→40 fly-in. (Previously the boot fov
    // came from the generic `cfg.fov`, later re-derived against the BAKED Blender camera's
    // aspect — a subtly different value that the controller then visibly eased across.)
    this.baseFov = cfg.focalLength
      ? this.computeFovForFocalLength(cfg.focalLength, sizes.aspect)
      : cfg.fov;
    if (cfg.focalLength) this.referenceAspect = sizes.aspect;
    this.instance = new THREE.PerspectiveCamera(this.baseFov, sizes.aspect, cfg.near, cfg.far);
    this.instance.position.copy(toVector3(cfg.start.position));
    this.instance.lookAt(toVector3(cfg.start.target));
    this.resize(sizes);
  }

  resize(sizes: SizesState): void {
    this.instance.aspect = sizes.aspect;
    this.instance.fov = this.computeFov(sizes.aspect, sizes.device);
    this.instance.updateProjectionMatrix();
  }

  /**
   * Resolve the render FOV. With a Blender reference aspect set we keep the camera's
   * exact authored perspective and only widen the vertical FOV on viewports NARROWER
   * than the authored frame (so the Blender horizontal composition is never cropped) —
   * we never add extra perspective on top of it. Without a reference we fall back to
   * the responsive mobile widen.
   */
  private computeFov(aspect: number, device: SizesState['device']): number {
    if (this.referenceAspect !== null) {
      if (aspect >= this.referenceAspect) return this.baseFov;
      const tanV = Math.tan(THREE.MathUtils.degToRad(this.baseFov) / 2);
      return THREE.MathUtils.radToDeg(2 * Math.atan((tanV * this.referenceAspect) / aspect));
    }
    return device === 'mobile' ? this.baseFov * settings.camera.mobileFovMultiplier : this.baseFov;
  }

  /** Adopt a different base FOV (e.g. from the Blender camera) so it survives resizes. */
  setBaseFov(fov: number): void {
    this.baseFov = fov;
    this.instance.fov = this.computeFov(this.instance.aspect, 'desktop');
    this.instance.updateProjectionMatrix();
  }

  /** Use the Blender camera's exact FOV + authored aspect so its framing is preserved. */
  useAuthoredFraming(fov: number, aspect: number): void {
    this.baseFov = fov;
    this.referenceAspect = aspect;
    this.instance.fov = this.computeFov(this.instance.aspect, 'desktop');
    this.instance.updateProjectionMatrix();
  }

  /**
   * Derive the base (vertical) FOV from a real focal length (mm) at the authored
   * aspect, on a 36mm full-frame gauge — so "35mm" gives the expected lens framing
   * regardless of the GLB's exported FOV.
   */
  setFocalLength(focalLength: number, aspect: number): void {
    this.baseFov = this.computeFovForFocalLength(focalLength, aspect);
    this.referenceAspect = aspect;
    this.instance.fov = this.computeFov(this.instance.aspect, 'desktop');
    this.instance.updateProjectionMatrix();
  }

  /** Pure FOV computation for a given focal length (same 36mm full-frame gauge as
   *  {@link setFocalLength}) WITHOUT mutating base state or snapping the camera —
   *  for live per-section focal-length changes that should ease through
   *  `CameraController`'s own smoothing rather than snap instantly. */
  computeFovForFocalLength(focalLength: number, aspect = this.instance.aspect): number {
    const filmGauge = 36; // full-frame width (mm)
    const filmHeight = filmGauge / Math.max(aspect, 1);
    const vFov = 2 * Math.atan(filmHeight / (2 * focalLength));
    return THREE.MathUtils.radToDeg(vFov);
  }

  /** Exact inverse of {@link computeFovForFocalLength} — the focal length (mm) a vertical FOV
   *  corresponds to on the same 36mm gauge. Used by the continuous magnet zoom, which
   *  interpolates in FOCAL space (log-ratio) rather than FOV degrees: a constant zoom *ratio*
   *  per unit progress is how a real lens pull moves, and it is what reads as natural. */
  focalLengthForFov(fov: number, aspect = this.instance.aspect): number {
    const filmGauge = 36;
    const filmHeight = filmGauge / Math.max(aspect, 1);
    return filmHeight / (2 * Math.tan(THREE.MathUtils.degToRad(fov) / 2));
  }
}
